import { differenceInSeconds, format, formatDistanceToNow } from "date-fns";
import { AlertTriangle, ArrowRight, Calendar, CheckCircle, Clock, FileX, RotateCcw, StopCircle, Trash2 } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useMutation } from "react-relay";
import { Button } from "src/components/ui/button";
import { useToast } from "src/hooks/use-toast";

import { DeleteScheduledDigestRunMutation } from "../mutations/DeleteScheduledDigestRunMutation";
import { RerunJobMutation } from "../mutations/RerunJobMutation";
import { getEffectiveJobRunStatus, isJobRunTimedOut } from "../utils";

interface JobRunData {
  id: string;
  status: string;
  scheduledRunDate?: number; // For scheduled runs
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  updatedAt: string;
}

interface DigestJobRunRowProps {
  jobRun: JobRunData;
  jobId?: string; // For deleting scheduled runs
  onRefresh: () => void;
  onCancel?: (jobRunId: string) => void;
  totalSections?: number;
}

// Helper function to format status text in human readable format
const formatStatus = (status: string, updatedAt: string, currentTime?: number) => {
  const effectiveStatus = getEffectiveJobRunStatus(status, updatedAt, currentTime);
  const isTimeout = isJobRunTimedOut(status, updatedAt, currentTime);

  switch (effectiveStatus) {
    case "COMPLETED":
      return "Completed";
    case "IN_PROGRESS":
      return "In Progress";
    case "ERRORED":
      return isTimeout ? "Errored" : "Errored";
    case "STOPPED":
      return "Stopped";
    case "PENDING":
      return "Pending";
    case "SCHEDULED":
      return "Scheduled";
    default:
      return effectiveStatus;
  }
};

// Helper function to format seconds into human-readable duration
const formatDurationFromSeconds = (seconds: number) => {
  if (seconds < 1) {
    return "< 1s";
  } else if (seconds < 60) {
    return `${seconds}s`;
  } else if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
  } else {
    const hours = Math.floor(seconds / 3600);
    const remainingMinutes = Math.floor((seconds % 3600) / 60);
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  }
};

// Helper function to format duration (stable version that doesn't use new Date())
const formatDuration = (startTime: string | null, completedTime: string | null, updatedTime: string | null, status: string, currentTime?: number) => {
  // Check if this is a timed out job
  if (updatedTime && isJobRunTimedOut(status, updatedTime, currentTime)) {
    return "Timed Out";
  }

  if (!startTime || status === "PENDING") {
    return "Running...";
  }

  // If completed_at is not set, calculate duration from current time for IN_PROGRESS, updated_at for others
  if (!completedTime) {
    let endTime;
    if (status === "IN_PROGRESS") {
      // For in progress jobs, use provided current time or fallback to Date.now()
      endTime = currentTime ? new Date(currentTime) : new Date();
    } else {
      // For other statuses (ERRORED, STOPPED), use updated_at
      endTime = updatedTime ? new Date(updatedTime) : null;
    }

    if (!endTime) {
      return "Running...";
    }

    const seconds = differenceInSeconds(endTime, new Date(startTime));
    const durationText = formatDurationFromSeconds(seconds);

    // Only show "(In Progress)" if status is actually IN_PROGRESS
    return status === "IN_PROGRESS" ? `${durationText} (In Progress)` : durationText;
  }

  // Use completed_at for final duration
  const seconds = differenceInSeconds(new Date(completedTime), new Date(startTime));
  return formatDurationFromSeconds(seconds);
};

const DigestJobRunRow: React.FC<DigestJobRunRowProps> = React.memo(function DigestJobRunRow({
  jobRun,
  jobId,
  onRefresh,
  onCancel,
  totalSections,
}) {
  const { toast } = useToast();
  const [rerunJob, isRerunInFlight] = useMutation(RerunJobMutation);
  const [deleteScheduledDigestRun, isDeleteInFlight] = useMutation(DeleteScheduledDigestRunMutation);
  const [currentTime, setCurrentTime] = useState<number>(Date.now());

  // Set up timer for in-progress jobs to update duration every minute
  useEffect(() => {
    // Check if job is actually in progress (we can use Date.now() here since it's only for setup)
    const isInProgress = jobRun.status === "IN_PROGRESS" && !jobRun.completedAt;

    if (isInProgress) {
      const interval = setInterval(() => {
        setCurrentTime(Date.now());
      }, 60000); // Update every minute instead of every render

      return () => clearInterval(interval);
    }

    return () => {}; // Return empty cleanup function when not in progress
  }, [jobRun.status, jobRun.completedAt]);

  // Helper function to check if a job run can be rerun
  const canRerun = (status: string, updatedAt: string) => {
    const effectiveStatus = getEffectiveJobRunStatus(status, updatedAt, currentTime);
    return effectiveStatus === "ERRORED" || effectiveStatus === "STOPPED";
  };

  // Handle rerun job
  const handleRerun = (jobRunId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent row click
    rerunJob({
      variables: {
        input: {
          jobRunId: jobRunId
        }
      },
      onCompleted: (response: any) => {
        if (response.rerunJob?.success) {
          toast({
            title: "Digest Agent rerun started",
            description: "The digest agent has been successfully rerun.",
            variant: "success",
          });
          // Add delay to allow backend to process the async rerun before refetching
          setTimeout(() => {
            onRefresh();
          }, 1000);
        } else {
          toast({
            title: "Error",
            description: "Failed to rerun the digest agent. Please try again.",
            variant: "error",
          });
        }
      },
      onError: (error) => {
        toast({
          title: "Error",
          description: error.message || "An unexpected error occurred",
          variant: "error",
        });
      }
    });
  };

  // Handle delete scheduled run
  const handleDeleteScheduledRun = async (scheduledRunDate: number, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent row click

    if (!jobId) {
      toast({
        title: "Error",
        description: "Cannot delete scheduled run: job ID is missing.",
        variant: "error",
      });
      return;
    }

    deleteScheduledDigestRun({
      variables: {
        input: {
          jobId: jobId,
          runDate: scheduledRunDate
        }
      },
      onCompleted: (response: any) => {
        if (response.deleteScheduledDigestRun?.success) {
          toast({
            title: "Scheduled run deleted",
            description: "The scheduled run has been successfully removed.",
            variant: "success",
          });
          // Refresh the list
          setTimeout(() => {
            onRefresh();
          }, 500);
        } else {
          toast({
            title: "Error",
            description: "Failed to delete the scheduled run. Please try again.",
            variant: "error",
          });
        }
      },
      onError: (error) => {
        toast({
          title: "Error",
          description: error.message || "An unexpected error occurred",
          variant: "error",
        });
      }
    });
  };

  const handleStop = (event: React.MouseEvent) => {
    event.stopPropagation();
    onCancel?.(jobRun.id);
  };

  const isScheduled = jobRun.status === "SCHEDULED";
  const timestamp = jobRun.startedAt || jobRun.createdAt;
  const effectiveStatus = getEffectiveJobRunStatus(jobRun.status, jobRun.updatedAt, currentTime);
  const canRerunJob = canRerun(jobRun.status, jobRun.updatedAt);
  const canStopJob = effectiveStatus === "IN_PROGRESS";

  // Allow clicking into completed, stopped, and in-progress runs
  const isCompleted = effectiveStatus === "COMPLETED" && !isScheduled;
  const isStopped = effectiveStatus === "STOPPED" && !isScheduled;
  const isClickable = isCompleted || isStopped || (effectiveStatus === "IN_PROGRESS" && !isScheduled);

  // Handle missing timestamp
  if (!timestamp) {
    return (
      <tr className="opacity-70">
        <td className="px-3 py-2 whitespace-nowrap">
          <div className="flex items-center">
            <span className="text-muted-foreground text-sm">Invalid job run</span>
          </div>
        </td>
        <td className="px-3 py-2 whitespace-nowrap">
          <span className="text-muted-foreground text-sm">No start time</span>
        </td>
        <td className="px-3 py-2 whitespace-nowrap hidden sm:table-cell">
          <span className="text-muted-foreground text-sm">-</span>
        </td>
      </tr>
    );
  }

  const handleClick = () => {
    if (isClickable) {
      const urlParams = new URLSearchParams(window.location.search);
      const onboardingParam = urlParams.get("onboarding");
      const queryString = onboardingParam ? `?onboarding=${onboardingParam}` : "";
      window.location.href = `/digests/run/${jobRun.id}${queryString}`;
    }
  };

  return (
    <tr
      className={`hover:bg-card/40 transition-colors duration-150 ${
        isClickable ? "hover:cursor-pointer" : "opacity-70"
      }`}
      onClick={handleClick}
    >
      <td className="px-3 py-2 whitespace-nowrap">
        <div className="flex items-center">
          {isScheduled ? (
            <Calendar className="w-4 h-4 text-blue-500 sm:mr-2" />
          ) : effectiveStatus === "COMPLETED" ? (
            <CheckCircle className="w-4 h-4 text-green-500 sm:mr-2" />
          ) : effectiveStatus === "IN_PROGRESS" ? (
            <Clock className="w-4 h-4 text-yellow-500 sm:mr-2" />
          ) : effectiveStatus === "ERRORED" ? (
            <AlertTriangle className="w-4 h-4 text-red-500 sm:mr-2" />
          ) : effectiveStatus === "STOPPED" ? (
            <StopCircle className="w-4 h-4 text-red-500 sm:mr-2" />
          ) : (
            <FileX className="w-4 h-4 text-red-500 sm:mr-2" />
          )}
          <span className="text-foreground text-sm hidden sm:inline">
            {formatStatus(jobRun.status, jobRun.updatedAt, currentTime)}
            {effectiveStatus === "IN_PROGRESS" && totalSections != null && totalSections > 0 && (
              <span className="text-muted-foreground ml-1">
                ({totalSections} {totalSections === 1 ? "section" : "sections"})
              </span>
            )}
          </span>
        </div>
      </td>
      <td className="px-3 py-2">
        <div className="flex items-center justify-between">
          <div className="text-foreground text-sm">
            {(() => {
              try {
                const date = new Date(timestamp);
                const utcDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
                return (
                  <div className="flex flex-col sm:flex-row sm:items-center">
                    <span className="whitespace-nowrap">{format(utcDate, "yyyy-MM-dd HH:mm")} UTC</span>
                    <span className="text-muted-foreground text-xs sm:ml-2">
                      ({format(date, "MM-dd HH:mm")} Local, {formatDistanceToNow(date, { addSuffix: true })})
                    </span>
                  </div>
                );
              } catch {
                return <span className="text-muted-foreground">Invalid date</span>;
              }
            })()}
          </div>
          {/* Mobile-only actions */}
          <div className="flex items-center gap-2 sm:hidden">
            {canStopJob && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleStop}
                className="h-6 px-1 text-red-400 hover:text-red-300 hover:border-red-400"
                title="Cancel run"
              >
                <StopCircle className="w-3 h-3" />
              </Button>
            )}
            {canRerunJob && (
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => handleRerun(jobRun.id, e)}
                disabled={isRerunInFlight}
                className="h-6 px-1"
              >
                <RotateCcw className="w-3 h-3" />
              </Button>
            )}
            {isScheduled && jobRun.scheduledRunDate && (
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => handleDeleteScheduledRun(jobRun.scheduledRunDate!, e)}
                disabled={isDeleteInFlight}
                className="h-6 px-2 text-red-400 hover:text-red-300 hover:border-red-400"
                title="Delete scheduled run"
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            )}
            {isClickable && <ArrowRight className="w-4 h-4 flex-shrink-0" />}
          </div>
        </div>
      </td>
      <td className="px-3 py-2 whitespace-nowrap hidden sm:table-cell">
        <div className="flex items-center justify-between">
          <span className="text-foreground text-sm">
            {isScheduled
              ? "-"
              : formatDuration(jobRun.startedAt || jobRun.createdAt, jobRun.completedAt, jobRun.updatedAt, jobRun.status, currentTime)}
          </span>
          <div className="flex items-center gap-2">
            {canStopJob && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleStop}
                className="h-6 px-1 text-red-400 hover:text-red-300 hover:border-red-400"
                title="Cancel run"
              >
                <StopCircle className="w-3 h-3" />
              </Button>
            )}
            {canRerunJob && (
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => handleRerun(jobRun.id, e)}
                disabled={isRerunInFlight}
                className="h-6 px-1"
              >
                <RotateCcw className="w-3 h-3" />
              </Button>
            )}
            {isScheduled && jobRun.scheduledRunDate && (
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => handleDeleteScheduledRun(jobRun.scheduledRunDate!, e)}
                disabled={isDeleteInFlight}
                className="h-6 px-2 text-red-400 hover:text-red-300 hover:border-red-400"
                title="Delete scheduled run"
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            )}
            {isClickable && <ArrowRight className="ml-2 flex-shrink-0" />}
          </div>
        </div>
      </td>
    </tr>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function to optimize re-renders
  // Only re-render if the actual job run data has changed
  return (
    prevProps.jobRun.id === nextProps.jobRun.id &&
    prevProps.jobRun.status === nextProps.jobRun.status &&
    prevProps.jobRun.createdAt === nextProps.jobRun.createdAt &&
    prevProps.jobRun.startedAt === nextProps.jobRun.startedAt &&
    prevProps.jobRun.completedAt === nextProps.jobRun.completedAt &&
    prevProps.jobRun.updatedAt === nextProps.jobRun.updatedAt &&
    prevProps.totalSections === nextProps.totalSections
  );
});

export default DigestJobRunRow;